'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { LocalizedLink } from '@/components/LocalizedLink';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from '@/hooks/useTranslations';
import { getPathForLocale } from '@/lib/i18n';
import {
  countActiveCasinoFilters,
  getCasinoCardDescription,
  getCasinoLogoAlt,
  isCasinoQuickFilterActive,
  sortCasinoCatalogItems,
  CASINO_QUICK_FILTER_IDS,
  type CasinoCatalogSort,
  type CasinoQuickFilterId,
} from '@/lib/casino-catalog';
import { getAutoExternalRel } from '@/lib/link-rel';
import { buildPriorityFilterSegments } from '@/lib/casino-filter-seo';
import { CasinoLogoPlaceholder } from '@/components/ThemedPlaceholders';
import { StarRatingBar } from '@/components/StarRating';
import { MotionReveal } from '@/components/MotionReveal';
import { MotionStagger } from '@/components/MotionStagger';

const COUNTRY_OPTIONS = ['USA', 'UK', 'Canada', 'Germany', 'Australia'];
const LICENSE_OPTIONS = ['MGA', 'UKGC', 'Curacao', 'Gibraltar'];
const PAYMENT_OPTIONS = ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Bitcoin'];

const FILTER_KEYS = ['country', 'license', 'paymentMethods', 'minDeposit', 'minRating'] as const;
const CASINOS_PER_PAGE = 18;
const ANIMATED_TILE_LIMIT = CASINOS_PER_PAGE;

type Casino = {
  id: string;
  cmsId?: number;
  name: string;
  slug: string;
  shortDescription: string;
  computedRating: number | string;
  ratingCount?: number;
  bonusShort: string;
  paymentMethods: string[];
  licenses?: string[];
  logoUrl?: string;
  visitUrl?: string;
};

type Props = {
  casinos: Casino[];
  locale: string;
  pageHeading: string;
  breadcrumbItems?: Array<{ label: string; href?: string }>;
  recommendedCasinos: Casino[];
  hasNoResults: boolean;
  forcedFilters?: {
    country?: string;
    license?: string;
    minRating?: string;
  };
};

function applyFilters(
  router: ReturnType<typeof useRouter>,
  locale: string,
  currentParams: URLSearchParams,
  updates: { country?: string; license?: string; minDeposit?: string; minRating?: string; paymentMethods?: string[] },
) {
  const params = new URLSearchParams(currentParams.toString());
  for (const key of FILTER_KEYS) params.delete(key);
  if (updates.minDeposit) params.set('minDeposit', updates.minDeposit);
  if (updates.paymentMethods?.length) params.set('paymentMethods', updates.paymentMethods.join(','));
  const qs = params.toString();

  const segments = buildPriorityFilterSegments({
    country: updates.country,
    license: updates.license,
    minRating: updates.minRating ? Number(updates.minRating) : undefined,
  });
  const basePath =
    segments.length > 0
      ? getPathForLocale(locale as 'en' | 'fr' | 'es' | 'ru', `/country/${segments.join('/')}`)
      : getPathForLocale(locale as 'en' | 'fr' | 'es' | 'ru', '/casinos');

  router.replace(qs ? `${basePath}?${qs}` : basePath, { scroll: false });
}

function FilterChip({ label, onRemove, removeLabel }: { label: string; onRemove: () => void; removeLabel: string }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[#ffb74d]/40 bg-[#ffb74d]/10 px-3 py-1.5 text-xs font-medium text-[#ffb74d] transition hover:bg-[#ffb74d]/20"
    >
      <span>{label}</span>
      <span aria-hidden>×</span>
      <span className="sr-only">{removeLabel}</span>
    </button>
  );
}

function QuickFilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex min-h-11 shrink-0 items-center rounded-full border px-4 py-2 text-xs font-medium transition ${
        active
          ? 'border-[#ffb74d] bg-[#ffb74d]/20 text-[#ffb74d]'
          : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:text-white'
      }`}
    >
      {label}
    </button>
  );
}

export function CasinosCatalog({
  casinos,
  locale,
  pageHeading,
  breadcrumbItems,
  recommendedCasinos,
  hasNoResults,
  forcedFilters,
}: Props) {
  const { t } = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [sortBy, setSortBy] = React.useState<CasinoCatalogSort>('featured');
  const [displayCount, setDisplayCount] = React.useState(CASINOS_PER_PAGE);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [brokenLogos, setBrokenLogos] = React.useState<Record<string, boolean>>({});
  const [country, setCountry] = React.useState(() => forcedFilters?.country ?? searchParams.get('country') ?? '');
  const [license, setLicense] = React.useState(() => forcedFilters?.license ?? searchParams.get('license') ?? '');
  const [minDeposit, setMinDeposit] = React.useState(() => searchParams.get('minDeposit') ?? '');
  const [minRating, setMinRating] = React.useState(() => forcedFilters?.minRating ?? searchParams.get('minRating') ?? '');
  const [selectedPayments, setSelectedPayments] = React.useState<string[]>(() => {
    const raw = searchParams.get('paymentMethods') ?? '';
    return raw ? raw.split(',').map((v) => v.trim()).filter(Boolean) : [];
  });

  React.useEffect(() => {
    setDisplayCount(CASINOS_PER_PAGE);
    setCountry(forcedFilters?.country ?? searchParams.get('country') ?? '');
    setLicense(forcedFilters?.license ?? searchParams.get('license') ?? '');
    setMinDeposit(searchParams.get('minDeposit') ?? '');
    setMinRating(forcedFilters?.minRating ?? searchParams.get('minRating') ?? '');
    const raw = searchParams.get('paymentMethods') ?? '';
    setSelectedPayments(raw ? raw.split(',').map((v) => v.trim()).filter(Boolean) : []);
  }, [searchParams, forcedFilters?.country, forcedFilters?.license, forcedFilters?.minRating]);

  const sortedCasinos = React.useMemo(
    () => sortCasinoCatalogItems(casinos, sortBy),
    [casinos, sortBy],
  );
  const visibleCasinos = sortedCasinos.slice(0, displayCount);
  const hasMore = displayCount < sortedCasinos.length;
  const animatedCasinos = visibleCasinos.slice(0, ANIMATED_TILE_LIMIT);
  const staticCasinos = visibleCasinos.slice(ANIMATED_TILE_LIMIT);

  const currentFilters = {
    country: country || undefined,
    license: license || undefined,
    minDeposit: minDeposit || undefined,
    minRating: minRating || undefined,
    paymentMethods: selectedPayments.length ? selectedPayments : undefined,
  };
  const hasActiveFilters =
    Boolean(country || license || minDeposit || minRating || selectedPayments.length > 0);
  const activeFilterCount = countActiveCasinoFilters(currentFilters);

  const crumbs =
    breadcrumbItems ?? [{ label: t('nav.home'), href: '/' }, { label: t('nav.casinos') }];

  const quickFilterLabel = (id: CasinoQuickFilterId) => {
    if (id === 'mga') return t('casinosPage.quickFilterMga');
    if (id === 'crypto') return t('casinosPage.quickFilterCrypto');
    return t('casinosPage.quickFilterLowDeposit');
  };

  const applyQuickFilter = (id: CasinoQuickFilterId) => {
    const base = getCurrentFilters();
    if (id === 'mga') {
      const nextLicense = license === 'MGA' ? '' : 'MGA';
      setLicense(nextLicense);
      applyFilters(router, locale, searchParams, { ...base, license: nextLicense || undefined });
      return;
    }
    if (id === 'crypto') {
      const nextPayments = selectedPayments.includes('Bitcoin')
        ? selectedPayments.filter((method) => method !== 'Bitcoin')
        : [...selectedPayments, 'Bitcoin'];
      setSelectedPayments(nextPayments);
      applyFilters(router, locale, searchParams, { ...base, paymentMethods: nextPayments });
      return;
    }
    const nextDeposit = minDeposit === '10' ? '' : '10';
    setMinDeposit(nextDeposit);
    applyFilters(router, locale, searchParams, { ...base, minDeposit: nextDeposit || undefined });
  };

  const handleCountryChange = (v: string) => {
    setCountry(v);
    applyFilters(router, locale, searchParams, { ...getCurrentFilters(), country: v || undefined });
  };
  const handleLicenseChange = (v: string) => {
    setLicense(v);
    applyFilters(router, locale, searchParams, { ...getCurrentFilters(), license: v || undefined });
  };
  const handleMinDepositChange = (v: string) => {
    setMinDeposit(v);
    applyFilters(router, locale, searchParams, { ...getCurrentFilters(), minDeposit: v || undefined });
  };
  const handleMinRatingChange = (v: string) => {
    setMinRating(v);
    applyFilters(router, locale, searchParams, { ...getCurrentFilters(), minRating: v || undefined });
  };
  const togglePayment = (method: string) => {
    const next = selectedPayments.includes(method)
      ? selectedPayments.filter((p) => p !== method)
      : [...selectedPayments, method];
    setSelectedPayments(next);
    applyFilters(router, locale, searchParams, { ...getCurrentFilters(), paymentMethods: next });
  };

  function getCurrentFilters() {
    return {
      country: country || undefined,
      license: license || undefined,
      minDeposit: minDeposit || undefined,
      minRating: minRating || undefined,
      paymentMethods: selectedPayments.length ? selectedPayments : undefined,
    };
  }

  const resetFilters = () => {
    setCountry('');
    setLicense('');
    setMinDeposit('');
    setMinRating('');
    setSelectedPayments([]);
    router.replace(
      getPathForLocale(locale as 'en' | 'fr' | 'es' | 'ru', '/casinos?allCountries=1'),
      { scroll: false },
    );
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    window.requestAnimationFrame(() => {
      setDisplayCount((c) => Math.min(c + CASINOS_PER_PAGE, sortedCasinos.length));
      setLoadingMore(false);
    });
  };

  const renderCasinoTile = (casino: Casino) => {
    const hasReviews = (casino.ratingCount ?? 0) > 0;
    const rating = Number(casino.computedRating);
    const safeRating = Number.isFinite(rating) ? Math.max(0, Math.min(5, rating)) : 0;
    const cardDescription = getCasinoCardDescription(casino);
    const logoAlt = getCasinoLogoAlt(casino.name);
    const paymentPreview = casino.paymentMethods.slice(0, 4);
    const licensePreview = (casino.licenses ?? []).slice(0, 2);

    return (
      <article
        key={casino.id}
        className="rounded-2xl border border-white/10 bg-[#1e2235]/80 p-4 shadow-[0_12px_30px_rgba(7,9,20,0.45)] sm:rounded-3xl sm:p-5"
      >
        <div className="mb-3 flex items-center gap-3 sm:mb-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-[#15192b] sm:h-14 sm:w-14 sm:rounded-xl">
            {casino.logoUrl && !brokenLogos[casino.id] ? (
              <Image
                src={casino.logoUrl}
                alt={logoAlt}
                title={casino.name}
                width={64}
                height={64}
                sizes="64px"
                className="h-full w-full object-contain p-2"
                loading="lazy"
                onError={() =>
                  setBrokenLogos((prev) => ({
                    ...prev,
                    [casino.id]: true,
                  }))
                }
              />
            ) : (
              <CasinoLogoPlaceholder
                initials={
                  casino.name
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part.charAt(0).toUpperCase())
                    .join('') || 'C'
                }
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-semibold text-white sm:text-lg">
              <LocalizedLink href={`/casinos/${casino.slug}`} className="hover:text-[#ffb74d]">
                {casino.name}
              </LocalizedLink>
            </h3>
            {hasReviews ? (
              <div className="mt-1 flex items-center gap-2">
                <StarRatingBar rating={safeRating} />
                <span className="text-xs font-medium text-slate-300">
                  {safeRating.toFixed(1)}
                  <span className="text-slate-500"> ({casino.ratingCount})</span>
                </span>
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-500">{t('casinosPage.noReviewsYet')}</p>
            )}
          </div>
        </div>

        {cardDescription ? (
          <p className="line-clamp-2 text-sm text-slate-400">{cardDescription}</p>
        ) : null}

        {casino.bonusShort ? (
          <div className="mt-3 rounded-xl border border-white/5 bg-white/5 px-3 py-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {t('casino.welcomeBonus')}
            </span>
            <p className="mt-1 line-clamp-2 text-sm font-semibold text-[#ffb74d]">{casino.bonusShort}</p>
          </div>
        ) : null}

        {(licensePreview.length > 0 || paymentPreview.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {licensePreview.map((item) => (
              <span
                key={`${casino.id}-license-${item}`}
                className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-medium text-emerald-200"
              >
                {item}
              </span>
            ))}
            {paymentPreview.map((method) => (
              <span
                key={`${casino.id}-payment-${method}`}
                className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-slate-300"
              >
                {method}
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 flex flex-col gap-2 sm:mt-4 sm:flex-row">
          {casino.visitUrl ? (
            <a
              href={casino.visitUrl}
              target="_blank"
              rel={getAutoExternalRel(casino.visitUrl)}
              className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full bg-[#ffb74d] px-4 py-2.5 text-xs font-semibold text-[#4a2800] transition hover:-translate-y-0.5 sm:py-2"
            >
              {t('casinosPage.visitCasino')}
            </a>
          ) : (
            <span className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border border-white/10 px-4 py-2.5 text-xs font-semibold text-slate-500 sm:py-2">
              {t('casinosPage.visitCasino')}
            </span>
          )}
          <Link
            href={getPathForLocale(locale as 'en' | 'fr' | 'es' | 'ru', `/casinos/${casino.slug}`)}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-semibold text-slate-200 transition hover:bg-white/10 sm:py-2"
          >
            {t('casinosPage.details')}
          </Link>
        </div>
      </article>
    );
  };

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <div className="absolute -left-32 top-80 h-[300px] w-[300px] rounded-full bg-gradient-to-br from-purple-900/40 to-transparent blur-3xl" />
        <div className="absolute -right-40 top-20 h-[460px] w-[460px] rounded-full bg-gradient-to-bl from-blue-900/40 to-transparent blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-80px)] min-h-[calc(100dvh-80px)] w-full max-w-6xl px-4 sm:px-6">
        {mobileSidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm md:hidden"
            aria-label={t('games.hideFilters')}
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
        <aside
          className={`scrollbar-hide shrink-0 overflow-y-auto overflow-x-hidden border-r border-white/5 bg-[#0b0d19]/95 backdrop-blur-xl max-md:transition-[width,transform] max-md:duration-200 max-md:ease-out md:relative md:inset-auto md:z-10 md:w-[300px] md:translate-x-0 ${
            mobileSidebarOpen
              ? 'fixed inset-y-0 left-0 z-30 w-[min(300px,85vw)]'
              : 'w-0 overflow-hidden border-r-0 md:w-[300px] md:overflow-y-auto md:border-r'
          }`}
        >
          <div className="w-full max-w-[300px] min-w-0 p-4 sm:p-6">
            <div className="mb-6 flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-xl font-bold text-white">
                <svg className="h-5 w-5 text-[#ffb74d]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                  />
                </svg>
                {t('casinosPage.filters')}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="min-h-11 shrink-0 px-1 text-xs text-slate-400 underline hover:text-white"
                >
                  {t('filters.resetFilters')}
                </button>
                <button
                  type="button"
                  onClick={() => setMobileSidebarOpen(false)}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:text-white md:hidden"
                  aria-label={t('games.hideFilters')}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="mb-8">
              <label
                htmlFor="casino-filter-country"
                className="mb-4 block text-sm font-bold uppercase tracking-wider text-slate-300"
              >
                {t('filters.allCountries')}
              </label>
              <select
                id="casino-filter-country"
                value={country}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="filters-select min-h-11 w-full cursor-pointer rounded border border-white/10 bg-[#15192B] px-3 py-2 text-sm text-slate-100 focus:border-[#ffb74d] focus:outline-none"
              >
                <option value="">{t('filters.allCountries')}</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-8">
              <label
                htmlFor="casino-filter-license"
                className="mb-4 block text-sm font-bold uppercase tracking-wider text-slate-300"
              >
                {t('filters.allLicenses')}
              </label>
              <select
                id="casino-filter-license"
                value={license}
                onChange={(e) => handleLicenseChange(e.target.value)}
                className="filters-select min-h-11 w-full cursor-pointer rounded border border-white/10 bg-[#15192B] px-3 py-2 text-sm text-slate-100 focus:border-[#ffb74d] focus:outline-none"
              >
                <option value="">{t('filters.allLicenses')}</option>
                {LICENSE_OPTIONS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-8">
              <label
                htmlFor="casino-filter-deposit"
                className="mb-4 block text-sm font-bold uppercase tracking-wider text-slate-300"
              >
                {t('filters.depositUpTo')}
              </label>
              <select
                id="casino-filter-deposit"
                value={minDeposit}
                onChange={(e) => handleMinDepositChange(e.target.value)}
                className="filters-select min-h-11 w-full cursor-pointer rounded border border-white/10 bg-[#15192B] px-3 py-2 text-sm text-slate-100 focus:border-[#ffb74d] focus:outline-none"
              >
                <option value="">{t('filters.depositUpTo')}</option>
                {[10, 20, 50, 100, 200, 500].map((n) => (
                  <option key={n} value={n}>
                    ${n}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-8">
              <label
                htmlFor="casino-filter-rating"
                className="mb-4 block text-sm font-bold uppercase tracking-wider text-slate-300"
              >
                {t('filters.minRating')}
              </label>
              <select
                id="casino-filter-rating"
                value={minRating}
                onChange={(e) => handleMinRatingChange(e.target.value)}
                className="filters-select min-h-11 w-full cursor-pointer rounded border border-white/10 bg-[#15192B] px-3 py-2 text-sm text-slate-100 focus:border-[#ffb74d] focus:outline-none"
              >
                <option value="">{t('filters.minRating')}</option>
                {['3.0', '3.5', '4.0', '4.5', '5.0'].map((r) => (
                  <option key={r} value={r}>
                    {r}+
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-8">
              <p
                id="casino-filter-payments-label"
                className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-300"
              >
                {t('filters.paymentMethods')}
              </p>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="casino-filter-payments-label">
                {PAYMENT_OPTIONS.map((method) => {
                  const selected = selectedPayments.includes(method);
                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => togglePayment(method)}
                      className={`inline-flex min-h-11 cursor-pointer items-center rounded-full border px-3 py-2 text-xs transition ${
                        selected
                          ? 'border-[#ffb74d] bg-[#ffb74d]/20 text-[#ffb74d]'
                          : 'border-white/10 text-slate-300 hover:text-white'
                      }`}
                    >
                      {method}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1 p-4 pb-24 sm:p-6 sm:pb-24 md:p-10 md:pb-20 md:pl-6">
          <Breadcrumbs items={crumbs} truncateCurrent />

          <MotionReveal className="mb-6 sm:mb-8" immediate>
            <h1 className="mb-2 line-clamp-3 text-2xl font-bold leading-tight text-white sm:text-3xl md:text-4xl md:line-clamp-none">
              {pageHeading}
            </h1>
            <p className="max-w-2xl text-sm text-slate-400 sm:text-base">{t('casinosPage.description')}</p>
          </MotionReveal>

          {!hasNoResults && (
            <div className="mb-5 flex flex-col gap-3 sm:mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-slate-300">
                    {t('casinosPage.showingCount', {
                      visible: visibleCasinos.length,
                      total: casinos.length,
                    })}
                  </p>
                  {country ? (
                    <FilterChip
                      label={country}
                      onRemove={() => handleCountryChange('')}
                      removeLabel={t('casinosPage.removeFilter')}
                    />
                  ) : null}
                  {license ? (
                    <FilterChip
                      label={license}
                      onRemove={() => handleLicenseChange('')}
                      removeLabel={t('casinosPage.removeFilter')}
                    />
                  ) : null}
                  {minDeposit ? (
                    <FilterChip
                      label={`${t('filters.depositUpTo')}: $${minDeposit}`}
                      onRemove={() => handleMinDepositChange('')}
                      removeLabel={t('casinosPage.removeFilter')}
                    />
                  ) : null}
                  {minRating ? (
                    <FilterChip
                      label={`${minRating}+`}
                      onRemove={() => handleMinRatingChange('')}
                      removeLabel={t('casinosPage.removeFilter')}
                    />
                  ) : null}
                  {selectedPayments.map((method) => (
                    <FilterChip
                      key={method}
                      label={method}
                      onRemove={() => togglePayment(method)}
                      removeLabel={t('casinosPage.removeFilter')}
                    />
                  ))}
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="min-h-11 px-2 text-xs text-slate-400 underline hover:text-white"
                    >
                      {t('filters.resetFiltersCta')}
                    </button>
                  ) : null}
                </div>
                <label htmlFor="casino-sort" className="flex min-h-11 shrink-0 items-center gap-2 text-sm font-medium text-slate-200">
                  {t('casinosPage.sortBy')}
                  <select
                    id="casino-sort"
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value as CasinoCatalogSort);
                      setDisplayCount(CASINOS_PER_PAGE);
                    }}
                    className="filters-select min-w-[160px] min-h-11 cursor-pointer rounded-lg border border-white/10 bg-[#15192B] px-3 py-2 text-sm text-slate-100 transition-colors hover:border-white/20 focus:border-[#ffb74d] focus:outline-none focus:ring-1 focus:ring-[#ffb74d]/50"
                  >
                    <option value="featured">{t('casinosPage.sortFeatured')}</option>
                    <option value="rating">{t('casinosPage.sortRating')}</option>
                    <option value="new">{t('casinosPage.sortNew')}</option>
                    <option value="name">{t('casinosPage.sortName')}</option>
                  </select>
                </label>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t('casinosPage.quickFilters')}
                </span>
                {CASINO_QUICK_FILTER_IDS.map((id) => (
                  <QuickFilterChip
                    key={id}
                    label={quickFilterLabel(id)}
                    active={isCasinoQuickFilterActive(id, currentFilters)}
                    onClick={() => applyQuickFilter(id)}
                  />
                ))}
              </div>
            </div>
          )}

          {hasNoResults ? (
            <div className="rounded-xl border border-amber-400/30 bg-[#1e2235]/85 p-4 text-center sm:rounded-2xl sm:p-6">
              <h3 className="text-base font-semibold text-white sm:text-lg">{t('filters.nothingFoundTitle')}</h3>
              <p className="mt-2 text-sm text-slate-300">{t('filters.nothingFoundDescription')}</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
              >
                {t('filters.resetFiltersCta')}
              </button>
              {recommendedCasinos.length > 0 && (
                <div className="mt-6 text-left sm:mt-8">
                  <h4 className="text-sm font-semibold text-white sm:text-base">{t('filters.recommendedCasinosTitle')}</h4>
                  <p className="mt-1 text-sm text-slate-400">{t('filters.recommendedCasinosDescription')}</p>
                  <MotionStagger className="mt-4 grid gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3" staggerStep={0.06}>
                    {recommendedCasinos.map((casino) => renderCasinoTile(casino))}
                  </MotionStagger>
                </div>
              )}
            </div>
          ) : (
            <>
              {animatedCasinos.length > 0 ? (
                <MotionStagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-2" staggerStep={0.06}>
                  {animatedCasinos.map((casino) => renderCasinoTile(casino))}
                </MotionStagger>
              ) : null}
              {staticCasinos.length > 0 ? (
                <div
                  className={`grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-2 ${
                    animatedCasinos.length > 0 ? 'mt-4 sm:mt-6' : ''
                  }`}
                >
                  {staticCasinos.map((casino) => renderCasinoTile(casino))}
                </div>
              ) : null}
              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    aria-busy={loadingMore}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-white/20 bg-white/5 px-8 py-4 font-medium text-white transition-colors hover:bg-white/10 disabled:cursor-wait disabled:opacity-70"
                  >
                    {loadingMore ? t('casinosPage.loadingMore') : t('games.loadMore')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0b0d19]/95 p-3 backdrop-blur-xl md:hidden">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white"
        >
          {t('casinosPage.filters')}
          <span className="text-slate-400">
            · {t('casinosPage.activeFilters', { count: activeFilterCount })}
          </span>
        </button>
      </div>
    </div>
  );
}
